import React from 'react';
import {
  clearChunkReloadMarker,
  isChunkLoadError,
  tryReloadOnceForChunkError,
} from './lazyRouteError';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches failed lazy() imports (stale hashed chunks after deploy) and auto-reloads once.
 * Non-chunk errors get a visible recovery UI instead of an uncaught blank tree.
 */
export class LazyRouteErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    if (tryReloadOnceForChunkError(error)) return;
    console.error('[LazyRouteErrorBoundary]', error);
  }

  private handleReload = (): void => {
    clearChunkReloadMarker();
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Chunk path: reload already requested (or already attempted). Show a brief message.
    if (isChunkLoadError(error)) {
      return (
        <div
          data-testid="lazy-route-chunk-recovery"
          className="mx-auto max-w-lg px-6 py-16 text-center text-foreground"
        >
          <h1 className="font-brand-display text-2xl">Updating the page…</h1>
          <p className="mt-3 text-sand-400">
            A newer version of the site is available. Reloading.
          </p>
          <button
            type="button"
            className="mt-6 border border-earth-700 px-4 py-2 text-sm hover:bg-earth-800"
            onClick={this.handleReload}
          >
            Reload now
          </button>
        </div>
      );
    }

    return (
      <div
        data-testid="lazy-route-error"
        className="mx-auto max-w-lg px-6 py-16 text-center text-foreground"
      >
        <h1 className="font-brand-display text-2xl">Something went wrong</h1>
        <p className="mt-3 text-sand-400">
          This page failed to load. Try refreshing, or go back home.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            className="border border-earth-700 px-4 py-2 text-sm hover:bg-earth-800"
            onClick={this.handleReload}
          >
            Reload
          </button>
          <a href="/" className="border border-earth-700 px-4 py-2 text-sm hover:bg-earth-800">
            Home
          </a>
        </div>
      </div>
    );
  }
}
